import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import {
  useFirstTradeImage,
  useMediaSrcSet,
  useMediaUrl,
  useLookups,
  useTrades,
  type Lookups,
  type TradeListItem,
} from "../../api/hooks.ts";
import { useDeleteNote, useNotes, type Note } from "../../api/notes.ts";
import {
  badgeOf,
  formatDay,
  formatPips,
  formatR,
  formatSignedPips,
  formatTime,
  initials,
  labelDirection,
  labelImageKind,
  labelOutcome,
  labelStatus,
  toneOf,
  formatLot,
} from "../../domain/format.ts";
import { Icon } from "../../ui/Icon.tsx";
import { Modal } from "../../ui/Modal.tsx";
import { MiniNote, MiniTrade } from "../../ui/MiniCard.tsx";
import { StatusBlock } from "../../ui/StatusBlock.tsx";
import { useOwnerLabel } from "../auth/ownerLabel.ts";
import { usePermissions } from "../auth/permissions.ts";
import { byNewestFirst } from "./dayOrder.ts";
import { NoteModal } from "./NoteModal.tsx";
import { holdRepeat } from "./repeatTrade.ts";

/**
 * La bitacora: operaciones y notas en la MISMA linea de tiempo, agrupadas por
 * dia. Dos modos, como en la plantilla:
 *
 *   - **Lista**: el dia entero, tarjeta a tarjeta. Para leer.
 *   - **Ramas**: los dias como nodos de un tronco y el contenido del dia
 *     elegido al lado. Para buscar: con doscientas entradas, desplazarse deja
 *     de funcionar y lo que hace falta es saltar.
 *
 * El modo elegido se recuerda. Cambiar de vista cada vez que se entra es el
 * tipo de friccion que hace que una bitacora se abandone.
 *
 * Con `instrumentId` la misma linea de tiempo sirve la pagina de un simbolo:
 * el simbolo queda FIJADO y desaparece lo que ahi no tiene sentido —su
 * selector, porque ya esta elegido; la caja de escribir, porque es la de la
 * bitacora entera; y las notas, que no cuelgan de ningun simbolo y ahi
 * apareceria la de cualquier otro dia junto a operaciones que no son suyas.
 * Sin la prop, la pantalla se comporta exactamente como antes.
 */

type Mode = "lista" | "ramas";
type Kind = "all" | "trades" | "notes";
type ResultFilter = "all" | "win" | "loss" | "breakEven";

const MODE_KEY = "tl:mode";

/**
 * `?pendientes=` — DE DONDE VIENE LA CAMPANA.
 *
 * Cada aviso del panel de notificaciones lleva a esta pantalla con uno de estos
 * valores puesto, porque un aviso que no lleva a donde se resuelve es la mitad de
 * un aviso. El estado vive en la URL y no en un `useState` justo por eso: un
 * enlace tiene que poder traerlo de fuera.
 *
 * Dos de los cuatro los filtra el SERVIDOR y dos se acotan AQUI, y la diferencia
 * se dice en la pantalla en vez de esconderse:
 *
 *   - "abiertas" y "planificadas" son un `status` que el contrato acepta
 *     (`GET /trades?status=`), asi que el listado que llega ya viene filtrado y
 *     el recuento es el de verdad.
 *   - "sin revisar" es `status=closed` mas "sin nota de adherencia", y
 *     "innegociables" es una bandera por operacion. El contrato no ofrece
 *     ninguno de los dos, asi que se acotan sobre las paginas YA cargadas y la
 *     pantalla lo dice con esas palabras, igual que hace el mosaico de simbolos
 *     con su marca ambar. Si esto llega a doler, lo que hay que cambiar es el
 *     CONTRATO —dos filtros mas en el listado—, no esta pantalla.
 */
type Pending = "abiertas" | "planificadas" | "revision" | "innegociables";

const PENDING: Record<
  Pending,
  { title: string; status: string | undefined; narrowed: boolean }
> = {
  abiertas: {
    title: "Operaciones abiertas sin cerrar",
    status: "open",
    narrowed: false,
  },
  planificadas: {
    title: "Planes que quedaron sin ejecutar",
    status: "planned",
    narrowed: false,
  },
  revision: {
    title: "Operaciones cerradas sin revisar",
    status: "closed",
    narrowed: true,
  },
  innegociables: {
    title: "Operaciones con una regla innegociable rota",
    status: undefined,
    narrowed: true,
  },
};

function readPending(value: string | null): Pending | null {
  /* Se comprueba contra las claves y no se hace un cast: `?pendientes=cualquier`
     lo escribe cualquiera en la barra de direcciones, y un valor inventado tiene
     que caer en "sin filtro", no en un `undefined` recorriendo la pantalla. */
  return value !== null && Object.hasOwn(PENDING, value) ? (value as Pending) : null;
}

/** El acotado que el contrato no ofrece. Ver el comentario de `PENDING`. */
function narrowPending(
  items: readonly TradeListItem[],
  pending: Pending | null,
): readonly TradeListItem[] {
  if (pending === "revision") {
    return items.filter((item) => item.status === "closed" && item.adherenceScore === null);
  }

  if (pending === "innegociables") {
    return items.filter((item) => item.hasBrokenMandatoryRule);
  }

  return items;
}

const KINDS: { value: Kind; label: string }[] = [
  { value: "all", label: "Todo" },
  { value: "trades", label: "Operaciones" },
  { value: "notes", label: "Solo notas" },
];

const RESULTS: { value: ResultFilter; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "win", label: "Ganados" },
  { value: "loss", label: "Perdidos" },
  { value: "breakEven", label: "En tablas" },
];

type Item =
  | { kind: "trade"; id: string; day: string; at: string; trade: TradeListItem }
  | { kind: "note"; id: string; day: string; at: string; note: Note };

type Day = { date: string; items: Item[]; totalR: number; rated: number; trades: number };

export function Timeline(props: { instrumentId?: string | undefined }): ReactNode {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const pinned = props.instrumentId !== undefined;

  /* En la pagina de un simbolo no hay filtro de pendientes: esa pantalla
     contesta "que hice en este par", y la campana no lleva ahi. */
  const pending = pinned ? null : readPending(params.get("pendientes"));
  const pendingMeta = pending === null ? null : PENDING[pending];

  const [mode, setMode] = useState<Mode>(readMode);
  const [kind, setKind] = useState<Kind>("all");
  const [result, setResult] = useState<ResultFilter>("all");
  const [symbol, setSymbol] = useState("");
  const [openDay, setOpenDay] = useState<string | null>(null);
  /* `?nota=nueva` abre el modal. Lo manda el "+" de la barra inferior, que
     pregunta si se registra una operacion o una nota. */
  const [noteOpen, setNoteOpen] = useState<Note | "new" | null>(
    params.get("nota") === "nueva" ? "new" : null,
  );

  /* EL VISOR DE UNA NOTA VIVE AQUI, EN LA PAGINA, y no dentro de la tarjeta.
     Antes estaba dentro de `NotePost`, y la tarjeta compacta del modo ramas es
     OTRO componente: se quedo sin visor y pulsarla abria el modal de editar, que
     es exactamente el fallo que el propietario ya habia reportado para la lista
     -- "deberia abrir la nota para verla, no para editarla" -- y que volvio a
     reportar para ramas. Con el visor en un solo sitio las dos vistas no pueden
     volver a divergir. */
  const [noteReading, setNoteReading] = useState<Note | null>(null);

  /* EL EFECTO ES LO QUE HACE QUE EL "+" FUNCIONE ESTANDO YA EN LA BITACORA, y su
     falta era el fallo que vio el propietario: "al darle clic en nota no
     funciona, no hace lo que promete". El valor inicial de `useState` se calcula
     UNA sola vez, al montar. Estando ya en /trades -- que es de donde se pulsa el
     "+" casi siempre -- la navegacion a /trades?nota=nueva solo cambia la URL: el
     componente no se vuelve a montar, el inicializador no se repite y no pasaba
     absolutamente nada.

     Mira el parametro y no un contador de clics: asi el enlace tambien funciona
     pegado en la barra de direcciones o guardado como acceso directo. */
  useEffect(() => {
    if (params.get("nota") === "nueva") setNoteOpen("new");
  }, [params]);

  /* Un Mentor lee la bitacora y no escribe en ella (ADR 0005): aqui desaparecen
     la caja de escribir, el boton "Nueva" y las acciones de cada nota. El que
     dice no de verdad es el servidor; esto solo evita ofrecer un 403. */
  const { user: me, canWriteTrades } = usePermissions();

  const rail = useRef<HTMLElement>(null);

  const lookups = useLookups();
  const timeZone = lookups.data?.app.timeZone ?? "America/Guatemala";

  /* Lo que el filtro no va a pintar tampoco se pide: una peticion que nadie mira
     solo puede fallar, y su fallo no tiene por que tapar la pantalla. */
  const showTrades = pinned || kind !== "notes";
  /* Con un filtro de pendientes puesto, las notas se apagan: el filtro habla de
     operaciones, y una nota entre medias no esta ni pendiente ni resuelta. */
  const showNotes = !pinned && pending === null && kind !== "trades";

  /* El simbolo fijado manda sobre el desplegable: en la pagina de un simbolo no
     hay otro que elegir. */
  const instrumentId = props.instrumentId ?? (symbol === "" ? undefined : symbol);

  /* El resultado y el simbolo los filtra el SERVIDOR: filtrarlos aqui solo
     filtraria la pagina que ya tienes, y "Ganados" mentiria en cuanto hubiera
     mas de cincuenta operaciones. */
  const trades = useTrades(
    {
      ...(result === "all" ? {} : { outcome: result }),
      ...(instrumentId === undefined ? {} : { instrumentId }),
      ...(pendingMeta?.status === undefined ? {} : { status: pendingMeta.status }),
    },
    showTrades,
  );
  const notes = useNotes(showNotes);

  const days = useMemo(
    () =>
      group(
        showTrades
          ? narrowPending(trades.data?.pages.flatMap((page) => page.items) ?? [], pending)
          : [],
        showNotes ? (notes.data?.pages.flatMap((page) => page.items) ?? []) : [],
      ),
    [trades.data, notes.data, showTrades, showNotes, pending],
  );

  /* Cuantas operaciones se han MIRADO, antes de acotar. Es el numero que hace
     honesto el aviso de "sobre las N cargadas": sin el, una lista vacia por
     acotado se leeria como "no hay ninguna". */
  const loadedTrades = (trades.data?.pages ?? []).reduce(
    (total, page) => total + page.items.length,
    0,
  );

  const current = days.find((day) => day.date === openDay) ?? days[0];
  const counts = tally(days);

  /* El estado que se mira depende del filtro activo. Con "Solo notas" puesto, un
     fallo del endpoint de operaciones no borra una pantalla que no lo usa. */
  const loading = (showTrades && trades.isPending) || (showNotes && notes.isPending);
  const failure = (showTrades ? trades.error : null) ?? (showNotes ? notes.error : null);

  const hasMore =
    (showTrades && trades.hasNextPage) || (showNotes && notes.hasNextPage);
  const loadingMore = trades.isFetchingNextPage || notes.isFetchingNextPage;

  /** La pagina siguiente de cada listado que se este mostrando (SPEC 19.2). */
  function loadMore(): void {
    if (showTrades && trades.hasNextPage && !trades.isFetchingNextPage) {
      void trades.fetchNextPage();
    }

    if (showNotes && notes.hasNextPage && !notes.isFetchingNextPage) {
      void notes.fetchNextPage();
    }
  }

  /*
   * El carril de dias se completa como pestanas de verdad en vez de quitarle los
   * roles: es literalmente un selector de un panel, y con muchos dias moverse con
   * las flechas es mas rapido que tabular uno a uno. Lo que faltaba era el
   * `tabpanel` con su id, el `aria-controls`, el tabIndex que viaja con la
   * seleccion y estas flechas. Se aceptan las cuatro direcciones porque el
   * carril es vertical en escritorio y horizontal en movil.
   */
  function onRailKey(event: KeyboardEvent<HTMLElement>): void {
    const tabs = [...(rail.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [])];
    const at = tabs.findIndex((tab) => tab === document.activeElement);
    const last = tabs.length - 1;

    if (at === -1 || last < 0) return;

    const target =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? last
          : event.key === "ArrowUp" || event.key === "ArrowLeft"
            ? (at === 0 ? last : at - 1)
            : event.key === "ArrowDown" || event.key === "ArrowRight"
              ? (at === last ? 0 : at + 1)
              : -1;

    const day = days[target];
    const tab = tabs[target];

    if (day === undefined || tab === undefined) return;

    event.preventDefault();
    setOpenDay(day.date);
    tab.focus();
  }

  function changeMode(next: Mode): void {
    setMode(next);
    try {
      window.localStorage.setItem(MODE_KEY, next);
    } catch {
      // Sin sitio donde guardar: el modo dura lo que dure la pestana.
    }
  }

  /**
   * Un recuerdo al azar entre las operaciones YA cargadas. Releer lo de hace
   * tres meses es la mitad del valor, y por eso el sorteo crece con cada
   * "Cargar publicaciones anteriores": cada pagina mete mas dias en el bombo.
   */
  function shuffle(): void {
    const all = days.flatMap((day) => day.items).filter((item) => item.kind === "trade");
    const pick = all[Math.floor(Math.random() * all.length)];

    if (pick !== undefined) navigate(`/trades/${pick.id}`);
  }

  return (
    <div className={mode === "ramas" ? "timeline branch-on" : "timeline"}>
      {!pinned && canWriteTrades && (
        <div className="compose">
          <div className="top">
            <span className="avatar">{initials(me?.displayName)}</span>
            <Link className="fake" to="/trades/new">
              Que operaste hoy?
            </Link>
          </div>
          <div className="quick">
            <Link to="/trades/new">
              <Icon name="hash" />
              <span>Nueva operacion</span>
            </Link>
            <button type="button" onClick={() => setNoteOpen("new")}>
              <Icon name="msg" />
              <span>Escribir una nota</span>
            </button>
            <button type="button" onClick={shuffle}>
              <Icon name="shuffle" />
              <span>Aleatorio</span>
            </button>
          </div>
        </div>
      )}

      {/* Se dice QUE filtro esta puesto y como quitarlo. Un listado que llega
          filtrado desde otra pantalla y no lo confiesa se lee como una bitacora
          a la que le faltan operaciones. */}
      {pendingMeta !== null && (
        <p className="infobox tl-pending">
          <span>
            <b>{pendingMeta.title}.</b> Viniste desde la campana.{" "}
            {pendingMeta.narrowed
              ? `Se busca sobre las ${loadedTrades} ${
                  loadedTrades === 1 ? "operacion cargada" : "operaciones cargadas"
                }${trades.hasNextPage ? ", y hay mas historial sin mirar." : ", que ya son todas."}`
              : "El listado viene filtrado por el servidor, asi que estan todas."}
          </span>
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => {
              /* Se borra SOLO este parametro y se deja el resto de la query:
                 quitar un filtro no tiene por que tirar los demas. */
              const next = new URLSearchParams(params);

              next.delete("pendientes");
              setParams(next, { replace: true });
            }}
          >
            <Icon name="x" />
            Quitar el filtro
          </button>
        </p>
      )}

      <div className="tl-bar">
        {!pinned && (
          <div className="tl-types" role="group" aria-label="Que mostrar">
            {KINDS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={kind === option.value ? "chip active" : "chip"}
                aria-pressed={kind === option.value}
                onClick={() => setKind(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        <div className="tl-modes" role="group" aria-label="Modo de vista">
          <button
            type="button"
            className={mode === "lista" ? "active" : ""}
            aria-pressed={mode === "lista"}
            title="Ver en modo lista"
            onClick={() => changeMode("lista")}
          >
            <Icon name="feed" />
            <span>Lista</span>
          </button>
          <button
            type="button"
            className={mode === "ramas" ? "active" : ""}
            aria-pressed={mode === "ramas"}
            title="Ver en modo ramas"
            onClick={() => changeMode("ramas")}
          >
            <Icon name="branch" />
            <span>Ramas</span>
          </button>
        </div>

        {canWriteTrades && (
          <div className="tl-right">
            <Link className="btn primary sm" to="/trades/new">
              <Icon name="plus" />
              Nueva
            </Link>
          </div>
        )}
      </div>

      <div className="tl-filters" role="group" aria-label="Filtrar por resultado">
        {RESULTS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`chip ${result === option.value ? "active" : ""} ${kind === "notes" ? "off" : ""}`}
            aria-pressed={result === option.value}
            onClick={() => setResult(option.value)}
          >
            {option.label}
          </button>
        ))}
        <span className="spacer" />
        <span className="hint">
          {counts.trades} {counts.trades === 1 ? "operacion" : "operaciones"}
          {showNotes ? `, ${counts.notes} ${counts.notes === 1 ? "nota" : "notas"}` : ""}
          {hasMore ? " · hay mas sin cargar" : ""}
        </span>
        {!pinned && (
          <select
            className="select"
            aria-label="Filtrar por simbolo"
            value={symbol}
            disabled={kind === "notes"}
            onChange={(event) => setSymbol(event.target.value)}
          >
            <option value="">Todos los simbolos</option>
            {(lookups.data?.instruments ?? [])
              .filter((item) => item.isActive)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.code}
                </option>
              ))}
          </select>
        )}
      </div>

      <StatusBlock
        loading={loading}
        error={failure}
        empty={days.length === 0}
        emptyIcon="feed"
        emptyTitle={
          pinned
            ? "Todavia no has operado este simbolo."
            : pendingMeta !== null
              ? "Ninguna aqui."
              : "No hay publicaciones con esos filtros."
        }
        emptyText={
          pinned
            ? "Registra la primera y aqui quedara su historia, dia a dia."
            : pendingMeta === null
              ? "Prueba con otro simbolo, otro resultado, o escribe la primera entrada."
              : /* La campana cuenta sobre el historial ENTERO y aqui se busca
                   sobre lo cargado, asi que este hueco puede significar dos
                   cosas distintas. Se dicen las dos en vez de afirmar la
                   comoda. */
                pendingMeta.narrowed && trades.hasNextPage
                ? `No hay ninguna entre las ${loadedTrades} operaciones cargadas. Carga mas historial para seguir buscando.`
                : "Puede que ya las hayas resuelto: vuelve a abrir la campana y el aviso habra desaparecido."
        }
        emptyAction={
          canWriteTrades ? (
            <Link className="btn primary" to="/trades/new">
              <Icon name="plus" />
              Registrar una operacion
            </Link>
          ) : undefined
        }
        onRetry={() => {
          if (showTrades) void trades.refetch();
          if (showNotes) void notes.refetch();
        }}
      >
        {mode === "ramas" && current !== undefined ? (
          <div className="branch">
            <aside
              className="branch-rail"
              role="tablist"
              aria-label="Dias"
              ref={rail}
              onKeyDown={onRailKey}
            >
              {days.map((day) => (
                <button
                  key={day.date}
                  type="button"
                  role="tab"
                  id={`brday-${day.date}`}
                  aria-selected={day.date === current.date}
                  aria-controls="branch-panel"
                  tabIndex={day.date === current.date ? 0 : -1}
                  className={day.date === current.date ? "brday active" : "brday"}
                  onClick={() => setOpenDay(day.date)}
                >
                  <i className={`node ${day.rated === 0 ? "" : toneOf(day.totalR)}`} />
                  <span className="bd-l">
                    <b>{formatDay(day.date)}</b>
                    <small>
                      {day.items.length} {day.items.length === 1 ? "entrada" : "entradas"}
                    </small>
                  </span>
                  <span className="bd-r">
                    <b className={day.rated === 0 ? "muted" : toneOf(day.totalR)}>
                      {day.rated === 0 ? "—" : formatR(day.totalR)}
                    </b>
                    <small>
                      {day.trades} {day.trades === 1 ? "op." : "ops."}
                    </small>
                  </span>
                </button>
              ))}
            </aside>

            <div
              className="branch-main"
              id="branch-panel"
              role="tabpanel"
              aria-labelledby={`brday-${current.date}`}
            >
              <div className="branch-h">
                <div>
                  <h3>{formatDay(current.date)}</h3>
                  <p>
                    {current.items.length}{" "}
                    {current.items.length === 1 ? "publicacion" : "publicaciones"}
                  </p>
                </div>
                <div className="day-sum">
                  {current.rated === 0 ? (
                    <span className="muted">sin R</span>
                  ) : (
                    <b className={toneOf(current.totalR)}>{formatR(current.totalR)}</b>
                  )}
                </div>
              </div>

              <div className="mini-grid">
                {current.items.map((item) =>
                  item.kind === "trade" ? (
                    <MiniTrade key={item.id} trade={item.trade} />
                  ) : (
                    <MiniNote
                      key={item.id}
                      note={item.note}
                      timeZone={timeZone}
                      /* LEER, no editar, y sin pedir permiso de escritura: un
                         Mentor entra a leer la bitacora que acompana (ADR 0005)
                         y antes no podia ni abrir una nota. */
                      onOpen={() => setNoteReading(item.note)}
                    />
                  ),
                )}
              </div>
            </div>
          </div>
        ) : (
          days.map((day) => (
            <section className="day" key={day.date}>
              <header className="day-h">
                <div>
                  <b>{formatDay(day.date)}</b>
                  <span className="when">
                    {day.items.length} {day.items.length === 1 ? "publicacion" : "publicaciones"}
                  </span>
                </div>
                <div className="day-sum">
                  {day.rated === 0 ? (
                    <span className="muted">sin R</span>
                  ) : (
                    <b className={toneOf(day.totalR)}>{formatR(day.totalR)}</b>
                  )}
                </div>
              </header>

              {day.items.map((item) =>
                item.kind === "trade" ? (
                  <TradePost
                    key={item.id}
                    trade={item.trade}
                    lookups={lookups.data}
                    canWrite={canWriteTrades}
                  />
                ) : (
                  <NotePost
                    key={item.id}
                    note={item.note}
                    timeZone={timeZone}
                    lookups={lookups.data}
                    onRead={() => setNoteReading(item.note)}
                    onEdit={() => setNoteOpen(item.note)}
                    canWrite={canWriteTrades}
                  />
                ),
              )}
            </section>
          ))
        )}

        {/* El boton de la plantilla, tambien en modo ramas: ahi cada pagina son
            dias nuevos en el carril, y sin el la vista se quedaba en los
            primeros cincuenta registros para siempre. */}
        {hasMore && (
          <div className="load-more">
            <button
              type="button"
              className="btn ghost"
              disabled={loadingMore}
              onClick={loadMore}
            >
              {loadingMore ? <span className="spinner" /> : <Icon name="feed" />}
              {loadingMore ? "Cargando…" : "Cargar publicaciones anteriores"}
            </button>
          </div>
        )}
      </StatusBlock>

      {/* EL VISOR, uno para las dos vistas. `key` con el id dentro para que al
          pasar de una nota a otra el modal arranque limpio. */}
      {noteReading !== null && (
        <NoteReader
          key={noteReading.id}
          note={noteReading}
          timeZone={timeZone}
          canWrite={canWriteTrades}
          onClose={() => setNoteReading(null)}
          onEdit={() => {
            setNoteReading(null);
            setNoteOpen(noteReading);
          }}
        />
      )}

      {noteOpen !== null && (
        <NoteModal
          {...(noteOpen === "new" ? {} : { note: noteOpen })}
          onClose={() => {
            setNoteOpen(null);
            // Se quita `?nota=nueva` al cerrar: si se quedara, recargar o volver
            // atras reabriria el modal encima de lo que el usuario estuviera
            // haciendo.
            if (params.get("nota") !== null) {
              const limpio = new URLSearchParams(params);
              limpio.delete("nota");
              setParams(limpio, { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * La tarjeta de una operacion, como POST de la plantilla: con captura, la
 * primera va arriba y ancha, y debajo la linea de pips —riesgo, objetivo,
 * resultado— con el R al lado (ADR 0007). Sin captura, la tarjeta sigue igual.
 */
function TradePost(props: {
  trade: TradeListItem;
  lookups: Lookups | undefined;
  canWrite: boolean;
}): ReactNode {
  const { trade } = props;
  const long = trade.direction === "buy";
  const navigate = useNavigate();

  /* Si `localStorage` no esta disponible NO se navega: llevar al alta vacia
     despues de pulsar Repetir seria peor que no hacer nada, porque parece que el
     boton funciono. */
  function repeat(item: TradeListItem): void {
    if (holdRepeat(item)) navigate("/trades/new");
  }

  return (
    <article className={trade.hasBrokenMandatoryRule ? "post breach" : "post"}>
      <div className="post-h">
        <span className="sym">{trade.instrumentCode}</span>
        <div className="post-meta">
          <b>{labelStatus(trade.status)}</b>
          <span>{trade.entryCode}</span>
        </div>
        <div className="post-badges">
          <span className={long ? "dir long" : "dir short"}>
            <Icon name={long ? "up" : "down"} />
            {labelDirection(trade.direction)}
          </span>
          {trade.hasBrokenMandatoryRule && (
            <span className="badge breach">
              <Icon name="flame" />
              Regla rota
            </span>
          )}
          {/* Calidad del dato, en ambar: los pips escritos y los derivados
              de los precios no cuadran. No es dinero. */}
          {trade.pipsDisagree && <span className="tag warn">Pips por revisar</span>}
        </div>
      </div>

      <PostMedia tradeId={trade.id} />

      <div className="post-stats">
        <div>
          <small>Riesgo</small>
          <b>{formatPips(trade.plannedRiskPips)} pips</b>
        </div>
        <div>
          <small>Objetivo</small>
          <b>{formatPips(trade.plannedRewardPips)} pips</b>
        </div>
        <div>
          <small>Resultado</small>
          <b className={toneOf(trade.resultPips)}>
            {trade.resultPips === null ? "—" : `${formatSignedPips(trade.resultPips)} pips`}
          </b>
        </div>
        {/* El lotaje solo ocupa sitio si lo hay. Es el unico de esta fila que
            puede faltar sin que la operacion este incompleta, asi que no se le
            reserva hueco con un guion. */}
        {trade.lotSize !== null && (
          <div>
            <small>Lotaje</small>
            <b>{formatLot(trade.lotSize)}</b>
          </div>
        )}
        <div>
          <small>R</small>
          <b className={toneOf(trade.rMultiple)}>{formatR(trade.rMultiple)}</b>
        </div>
        <div>
          <small>Cierre</small>
          <b>
            {trade.outcome === null ? (
              "—"
            ) : (
              <span className={`badge ${badgeOf(trade.outcome)}`}>{labelOutcome(trade.outcome)}</span>
            )}
          </b>
        </div>
      </div>

      <div className="post-f">
        <Link to={`/trades/${trade.id}`}>
          <Icon name="eye" />
          <span>Ver detalle</span>
        </Link>
        <Link to={`/trades/${trade.id}`}>
          <Icon name="edit" />
          <span>Revisar</span>
        </Link>
        {/* REPETIR: deja el alta rellena con lo que no cambia -- simbolo, broker,
            direccion, estrategia, setup, temporalidad y el plan en pips -- y nada
            de lo que es de ESTA operacion: ni capturas, ni resultado, ni
            revision, ni titulo. El detalle de por que no se copia cada cosa esta
            en `repeatTrade.ts`.

            Solo con permiso de escritura: mirando la bitacora de otro, el boton
            llevaria a un alta que el servidor rechazaria con 403. */}
        {props.canWrite && (
          <button type="button" onClick={() => repeat(trade)}>
            <Icon name="copy" />
            <span>Repetir</span>
          </button>
        )}
        <span className="spacer" />
        <span className="revw">{trade.entryCode}</span>
      </div>
    </article>
  );
}

/**
 * La primera captura de la operacion, arriba y ancha, como el `.media` del feed
 * de la plantilla. Es el post: «ver como estaba el grafico» es lo que el
 * propietario abre la bitacora a mirar.
 *
 * Cuesta la misma peticion por tarjeta que la miniatura del modo ramas, y por
 * la misma razon: el contrato no trae las capturas en el listado. La clave de
 * cache es la del detalle, asi que abrir la operacion despues no vuelve a
 * pedirla. Sin captura no se pinta nada: la tarjeta se queda como estaba.
 */
function PostMedia(props: { tradeId: string }): ReactNode {
  const { images } = useFirstTradeImage(props.tradeId);
  const mediaUrl = useMediaUrl();
  const mediaSrcSet = useMediaSrcSet();
  const [shown, setShown] = useState(0);

  if (images.length === 0) return null;

  // El indice se acota al pintar y no al pulsar: si una captura se borra desde
  // otra pestana, la lista encoge y el indice guardado se quedaria fuera.
  const index = Math.min(shown, images.length - 1);
  const image = images[index];

  if (image === undefined) return null;

  const several = images.length > 1;

  // Las flechas viven DENTRO de un enlace que ocupa la tarjeta entera, asi que
  // tienen que detener el evento o pasar de captura abriria la operacion. Es el
  // mismo problema que la estrella del mosaico de Indices y pares, y se resuelve
  // igual: parar la propagacion y el comportamiento por defecto.
  function pasar(delta: number, event: MouseEvent<HTMLButtonElement>): void {
    // SOBRE EL EVENTO SINTETICO, NO SOBRE `event.nativeEvent`. Pararlo en el
    // nativo no sirve de nada y era el fallo que vio el propietario -- "cuando le
    // doy clic a los botones entra al post de trade en lugar de cambiar la
    // imagen" --: cuando este manejador corre, el evento nativo YA subio hasta la
    // raiz, que es donde React escucha, y React propaga despues por su propio
    // arbol. Ademas react-router consulta `defaultPrevented` del sintetico, y esa
    // marca solo se pone llamando a preventDefault AQUI.
    event.preventDefault();
    event.stopPropagation();
    setShown((current) => {
      const next = Math.min(current, images.length - 1) + delta;

      return (next + images.length) % images.length;
    });
  }

  return (
    <div className="media n1">
      <Link
        className="thumb"
        to={`/trades/${props.tradeId}`}
        aria-label={`Ver la operacion y su captura: ${labelImageKindOf(image.kind)}`}
      >
        {/* LAS DOS VARIANTES Y QUE ELIJA EL NAVEGADOR. Antes era `thumb` fija:
            400 px estirados a los ~722 que mide esta tarjeta en un portatil, y a
            casi el triple en un telefono de densidad alta. El propietario lo
            describio como "bien fea deteriorada", y lo era.

            `sizes` describe el ANCHO FINAL de este hueco, no el de la imagen: en
            movil la tarjeta ocupa el ancho de la pantalla menos el margen; de
            760 px arriba se queda en los 722 de la columna del feed. */}
        <img
          /* `src` de reserva, ademas del `srcset`. Un navegador que entienda
             `srcset` con descriptores `w` IGNORA este `src`, asi que no se
             descarga dos veces; esta para que un navegador que no lo entienda
             ensene algo en vez de un hueco roto. Se apunta a `view` y no a
             `thumb`: si hay que elegir a ciegas, mejor la buena. */
          src={mediaUrl(image.contentHash, "view")}
          srcSet={mediaSrcSet(image.contentHash, image.width)}
          sizes="(max-width: 760px) calc(100vw - 32px), 722px"
          width={image.width}
          height={image.height}
          loading="lazy"
          decoding="async"
          alt=""
        />
        <span className="cap">{labelImageKindOf(image.kind)}</span>

        {several && (
          <>
            <button
              type="button"
              className="media-nav prev"
              onClick={(event) => pasar(-1, event)}
              aria-label="Captura anterior"
            >
              <Icon name="prev" />
            </button>
            <button
              type="button"
              className="media-nav next"
              onClick={(event) => pasar(1, event)}
              aria-label="Captura siguiente"
            >
              <Icon name="next" />
            </button>
            {/* Cuantas quedan. Sin esto se pasa de captura sin saber si queda
                alguna, y se pulsa la flecha a ciegas. */}
            <span className="media-count">
              {index + 1} de {images.length}
            </span>
          </>
        )}
      </Link>
    </div>
  );
}

function labelImageKindOf(kind: string | null): string {
  return labelImageKind(kind);
}

/**
 * EL VISOR DE UNA NOTA. Uno solo, usado por la lista y por el modo ramas.
 *
 * Vivia dentro de `NotePost` y eso fue el fallo: la tarjeta compacta de ramas es
 * otro componente, se quedo sin visor y pulsarla abria el modal de EDITAR. El
 * propietario lo reporto dos veces, una por vista. Con el visor aqui, las dos
 * pantallas no pueden volver a decir cosas distintas.
 *
 * Ensena el texto ENTERO, con sus saltos de linea respetados y a tamano de
 * lectura; en las tarjetas se recorta. Editar es un verbo aparte y explicito:
 * una lectura que modifica por accidente es la peor clase de sorpresa en una
 * bitacora.
 */
function NoteReader(props: {
  note: Note;
  timeZone: string;
  canWrite: boolean;
  onClose: () => void;
  onEdit: () => void;
}): ReactNode {
  /* La nota la firma el DUENO de la bitacora, no quien la mira: `NoteDto` no
     trae autor, y mirando la de otro el de la sesion seria el nombre
     equivocado. */
  const owner = useOwnerLabel();

  return (
    <Modal
      narrow
      title="Nota"
      onClose={props.onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={props.onClose}>
            Cerrar
          </button>
          {/* Editar SOLO desde aqui y solo si se puede escribir en esta bitacora.
              Mirando la de otro, `canWrite` es falso y el verbo no existe: el
              servidor responderia 404 de todas formas, pero ofrecer lo que no se
              puede hacer es prometer en falso. */}
          {props.canWrite && (
            <button type="button" className="btn primary" onClick={props.onEdit}>
              <Icon name="edit" />
              Editar
            </button>
          )}
        </>
      }
    >
      <p className="tw-read">{props.note.text}</p>

      <p className="hint">
        {owner.label} · {formatTime(props.note.postedAtUtc, props.timeZone)}
      </p>
    </Modal>
  );
}

function NotePost(props: {
  note: Note;
  timeZone: string;
  lookups: Lookups | undefined;
  onRead: () => void;
  onEdit: () => void;
  canWrite: boolean;
}): ReactNode {
  const remove = useDeleteNote();
  /* Leer ya no es estado de esta tarjeta: el visor vive en la pagina y lo
     comparte con el modo ramas. Aqui solo queda el borrado, que si es de la
     tarjeta porque borra ESTA nota. */
  const [doomed, setDoomed] = useState(false);
  /* La nota la firma el DUENO de la bitacora, no quien la mira: `NoteDto` no
     trae autor, y mirando la de otro el de la sesion seria el nombre equivocado. */
  const owner = useOwnerLabel();
  const mood = (props.lookups?.catalogs["emociones"] ?? []).find(
    (item) => item.id === props.note.moodId,
  );

  return (
    <article className="post tweet">
      <div className="post-h">
        <span className="avatar sm">{initials(owner.initials)}</span>
        <div className="post-meta">
          <b>{owner.label}</b>
          <span>{formatTime(props.note.postedAtUtc, props.timeZone)}</span>
        </div>
        <div className="post-badges">
          <span className="ttag">
            <Icon name="msg" />
            Nota
          </span>
        </div>
      </div>

      {/* Pulsar la nota la abre para LEERLA, no para editarla. Lo pidio el
          propietario: "deberia abrir la nota para verla mejor y mas ampliado,
          pero no para editarla". Editar es un verbo aparte y explicito: una
          lectura que modifica por accidente es la peor clase de sorpresa en una
          bitacora. */}
      <button type="button" className="tw-open" onClick={props.onRead}>
        <p className="tw-text">{props.note.text}</p>
        <span className="tw-more">Leer la nota</span>
      </button>

      {mood !== undefined && (
        <div className="post-emos">
          <span className={`emo ${mood.polarity === "positive" ? "good" : mood.polarity === "negative" ? "bad" : "warn"}`}>
            {mood.emoji !== null && <span aria-hidden="true">{mood.emoji}</span>} {mood.name}
          </span>
        </div>
      )}

      {props.canWrite && (
        <div className="post-f">
          <button type="button" onClick={props.onEdit}>
            <Icon name="edit" />
            <span>Editar</span>
          </button>
          <span className="spacer" />
          <button
            type="button"
            className="revw"
            disabled={remove.isPending}
            onClick={() => setDoomed(true)}
          >
            <Icon name="trash" />
            Eliminar
          </button>
        </div>
      )}

      {doomed && (
        <Modal
          narrow
          title="Eliminar la nota"
          onClose={() => setDoomed(false)}
          footer={
            <>
              <button type="button" className="btn ghost" onClick={() => setDoomed(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn danger"
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(props.note.id, { onSuccess: () => setDoomed(false) })
                }
              >
                {remove.isPending ? <span className="spinner" /> : <Icon name="trash" />}
                {remove.isPending ? "Eliminando…" : "Eliminar la nota"}
              </button>
            </>
          }
        >
          {/* Antes se borraba al primer clic, sin preguntar. Una nota es texto
              escrito a mano que no esta en ningun otro sitio: perderla por un
              toque de mas en el movil no tiene vuelta atras. */}
          <p className="warnbox">
            <b>Esto no se puede deshacer.</b> La nota no esta en ninguna otra parte.
          </p>

          <p className="tw-read">{props.note.text}</p>
        </Modal>
      )}
    </article>
  );
}

function readMode(): Mode {
  try {
    return window.localStorage.getItem(MODE_KEY) === "ramas" ? "ramas" : "lista";
  } catch {
    return "lista";
  }
}

function tally(days: readonly Day[]): { trades: number; notes: number } {
  let trades = 0;
  let notes = 0;

  for (const day of days) {
    for (const item of day.items) {
      if (item.kind === "trade") trades += 1;
      else notes += 1;
    }
  }

  return { trades, notes };
}

/**
 * Mezcla operaciones y notas en dias. Las dos listas llegan ya ordenadas del
 * servidor (dia descendente), asi que el orden de LOS DIAS es una fusion y no
 * una ordenacion: reordenarlos aqui romperia la paginacion por cursor
 * (SPEC 19.2).
 *
 * DENTRO DE CADA DIA SI SE ORDENA, por hora y de mas reciente a mas antiguo.
 * Antes no: se metian primero todas las operaciones y despues todas las notas,
 * asi que una nota escrita a las 18:39 caia por debajo de una operacion abierta a
 * las 22:05. El propietario lo dijo con la frase que lo explica entero: «se
 * supone que esto es una linea de tiempo» (BUGLOG 2026-10-16).
 *
 * Esto NO toca la paginacion: los dias siguen llegando y quedando en el orden del
 * servidor, y lo que se ordena es lo que ya esta dentro de cada uno.
 */
function group(trades: readonly TradeListItem[], notes: readonly Note[]): Day[] {
  const byDate = new Map<string, Item[]>();

  const push = (date: string, item: Item): void => {
    const bucket = byDate.get(date);

    if (bucket === undefined) byDate.set(date, [item]);
    else bucket.push(item);
  };

  for (const trade of trades) {
    push(trade.tradeDateLocal, {
      kind: "trade",
      id: trade.id,
      day: trade.tradeDateLocal,
      /* La apertura, y el plan mientras no la haya: es la MISMA regla con la que
         el servidor eligio el dia de la operacion. Ordenar dentro del dia por un
         momento distinto del que eligio el dia serian dos relojes para lo
         mismo. */
      at: trade.openedAtUtc ?? trade.plannedAtUtc,
      trade,
    });
  }

  for (const note of notes) {
    push(note.noteDateLocal, {
      kind: "note",
      id: note.id,
      day: note.noteDateLocal,
      at: note.postedAtUtc,
      note,
    });
  }

  return [...byDate.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, items]) => {
      items.sort(byNewestFirst);

      let totalR = 0;
      let rated = 0;
      let count = 0;

      for (const item of items) {
        if (item.kind !== "trade") continue;

        count += 1;

        if (item.trade.rMultiple !== null) {
          totalR += item.trade.rMultiple;
          rated += 1;
        }
      }

      return { date, items, totalR: Math.round(totalR * 100) / 100, rated, trades: count };
    });
}
